/**
 * Étiquettes de diffusion — lot AV3.
 *
 * L'ÉTIQUETTE dit le portail, la COULEUR dit l'état. Étiquettes texte, pas de logo :
 * SL · LBC · CPME. Le site vitrine se distingue par un globe : c'est le canal de l'agence,
 * pas un portail tiers.
 *
 * 🔴 Un support JAMAIS ENGAGÉ n'apparaît pas. Pas de rangée de pastilles éteintes : sur un
 * portefeuille où la grande majorité des biens ne sont diffusés nulle part, quatre pastilles
 * grises par carte ne seraient que du bruit. Un bien sans aucun support engagé affiche
 * « Non diffusé », en texte, UNE seule fois.
 *
 * 🔴 « Modifié, non renvoyé » = ambre POINTILLÉ. ⛔ Jamais de violet : dans Komerces le violet
 * est la couleur de l'IA (DESIGN_SYSTEM §1, décision #3) — un état de diffusion en violet se
 * lirait comme une suggestion de l'IA.
 */
import React from 'react';

export const ETAT_EN_LIGNE = 'en_ligne';
export const ETAT_ENVOI = 'envoi_en_cours';
export const ETAT_MODIFIE = 'modifie_non_renvoye';
export const ETAT_REFUSE = 'refuse';

// Un mot par support, l'ordre d'affichage est celui-ci.
const SUPPORTS = [
  { id: 'seloger', court: 'SL', nom: 'SeLoger' },
  { id: 'leboncoin', court: 'LBC', nom: 'LeBonCoin' },
  { id: 'cessionpme', court: 'CPME', nom: 'CessionPME' },
  { id: 'site_vitrine', court: null, icone: 'bi-globe', nom: 'Site vitrine' },
];

const STYLES = {
  [ETAT_EN_LIGNE]: { cls: 'border-emerald-300 bg-emerald-50 text-emerald-800', mot: 'en ligne' },
  [ETAT_ENVOI]: { cls: 'border-amber-400 bg-amber-100 text-amber-900', mot: 'envoi en cours' },
  [ETAT_MODIFIE]: { cls: 'border-amber-500 border-dashed bg-white text-amber-800', mot: 'modifié — non renvoyé' },
  [ETAT_REFUSE]: { cls: 'border-red-300 bg-red-50 text-red-700', mot: 'refusé' },
};

/** Le bien a-t-il au moins un support modifié et non renvoyé ? */
export const aUnSupportModifie = (etats) =>
  Object.values(etats || {}).some(v => v === ETAT_MODIFIE);

/** Les supports modifiés de ce bien (sert au renvoi en lot). */
export const supportsModifies = (etats) =>
  Object.entries(etats || {}).filter(([, v]) => v === ETAT_MODIFIE).map(([s]) => s);

export const DiffusionBadges = ({ etats, propertyId }) => {
  const engages = SUPPORTS.filter(s => (etats || {})[s.id]);
  if (engages.length === 0) {
    return (
      <span className="text-xs text-slate-400" data-testid={`diffusion-none-${propertyId}`}>
        Non diffusé
      </span>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-1" data-testid={`diffusion-badges-${propertyId}`}>
      {engages.map((s) => {
        const etat = etats[s.id];
        const style = STYLES[etat] || STYLES[ETAT_EN_LIGNE];
        return (
          <span
            key={s.id}
            // ⚠️ Sur mobile, aucune infobulle : la couleur et le texte suffisent, le détail est
            // sur la fiche. `title` reste inerte au doigt, il ne sert que la souris.
            title={`${s.nom} — ${style.mot}`}
            data-testid={`diffusion-badge-${s.id}-${propertyId}`}
            data-etat={etat}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-bold leading-none ${style.cls}`}
          >
            {s.court || <i className={`bi ${s.icone}`} />}
            {etat === ETAT_MODIFIE && <i className="bi bi-pencil-fill text-[8px]" />}
          </span>
        );
      })}
    </span>
  );
};

export default DiffusionBadges;
